import type { UsageModelRow, UsageWeek } from '../stores/usage'
import { cacheHitRate, formatUsd } from '../composables/usageFormatting'

// Pure helpers behind the /usage spend bars (Graphite & Signal redesign):
// operation tone, bar widths/heights, ISO week labels and the chart's
// accessible description. Kept out of the components so each rule has a
// direct unit test and the .vue files stay under the line limit.

const PERCENT = 100
// A non-zero week never draws as an invisible 0px bar.
const MIN_VISIBLE_BAR_PERCENT = 2
const MS_PER_DAY = 86_400_000
const DAYS_PER_WEEK = 7
const ISO_THURSDAY_OFFSET = 4

// Meaning colours: amber = agent work, violet = life (the CV generator),
// mint = every brain operation (ingest / lint / synthesis / query).
export function operationTone(operation: string): string {
  const op = operation.toLowerCase()
  if (op.includes('agent')) return 'bg-factory'
  if (op === 'cv') return 'bg-life'
  return 'bg-brand-primary'
}

// `value` as a percentage of `max`, clamped to 0–100. Zero max → 0.
export function shareOfMax(value: number, max: number): number {
  if (max <= 0 || value <= 0) return 0
  return Math.min(PERCENT, (value / max) * PERCENT)
}

// "Apr 13" from an ISO "YYYY-MM-DD" date, read as UTC so the label never
// slips a day in a negative-offset browser.
export function formatWeekStart(iso: string): string {
  return new Date(iso + 'T00:00:00Z').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

// ISO-8601 week number label ("W15") for the chart's x-axis — short
// enough for eight columns on a 390px phone.
export function isoWeekLabel(iso: string): string {
  const date = new Date(iso + 'T00:00:00Z')
  const weekday = date.getUTCDay() || DAYS_PER_WEEK
  date.setUTCDate(date.getUTCDate() + ISO_THURSDAY_OFFSET - weekday)
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1)
  const week = Math.ceil(((date.getTime() - yearStart) / MS_PER_DAY + 1) / DAYS_PER_WEEK)
  return `W${week}`
}

// Overall prompt-cache hit rate across every model this week — the
// per-model rates summed into one row, same formula as `cacheHitRate`.
export function aggregateCacheHitRate(models: UsageModelRow[]): number | null {
  const sum = (field: keyof Omit<UsageModelRow, 'model'>) => models.reduce((acc, m) => acc + m[field], 0)
  return cacheHitRate({
    model: 'all',
    input_tokens: sum('input_tokens'),
    output_tokens: sum('output_tokens'),
    cache_read_tokens: sum('cache_read_tokens'),
    cache_creation_tokens: sum('cache_creation_tokens'),
    estimated_cost_cents: sum('estimated_cost_cents'),
  })
}

export interface ChartWeek {
  weekStart: string
  label: string
  cents: number
  isCurrent: boolean
  heightPercent: number
}

// Bars for the "Previous weeks" chart, oldest → newest, with the live
// current week (when present) as the final, highlighted bar. Past weeks
// arrive newest-first from the API, hence the reverse.
export function chartWeeks(current: UsageWeek | null, past: UsageWeek[]): ChartWeek[] {
  const ordered = past.toReversed().concat(current ? [current] : [])
  const max = Math.max(0, ...ordered.map((w) => w.total_cost_cents))
  return ordered.map((w) => {
    const share = shareOfMax(w.total_cost_cents, max)
    return {
      weekStart: w.week_start,
      label: isoWeekLabel(w.week_start),
      cents: w.total_cost_cents,
      isCurrent: w === current,
      heightPercent: w.total_cost_cents > 0 ? Math.max(share, MIN_VISIBLE_BAR_PERCENT) : 0,
    }
  })
}

// The chart is one role="img"; this is its whole text alternative.
export function chartAriaLabel(weeks: ChartWeek[]): string {
  if (weeks.length === 0) return 'No weekly spend recorded yet'
  const cents = weeks.map((w) => w.cents)
  const range = `between ${formatUsd(Math.min(...cents))} and ${formatUsd(Math.max(...cents))}`
  const current = weeks.find((w) => w.isCurrent)
  const tail = current ? `; this week so far ${formatUsd(current.cents)}` : ''
  return `Weekly spend for the last ${weeks.length} week${weeks.length === 1 ? '' : 's'}, ${range}${tail}`
}
